// ========================================
// Tasks Admin View — Gestión de Tareas
// (Admin, Coordinador, Profesor)
// ========================================

const TasksAdminView = {
    _tasks: [],
    _pendingAttachment: null,
    _viewMode: 'tasks',
    _selectedTaskId: null,
    _searchQuery: '',
    _selectedFormationFilter: 'ALL',
    _showArchived: false,

    // Diseño C (2026-10-26): a la izquierda las tareas con su día de entrega; a la derecha la elegida con su
    // enunciado, material, destinatarios y sus entregas, sin abrir otra ventana. «Prácticas» sigue igual.
    // Estilos: assets/css/views/tasks.css (.tk-*).
    render() {
        const tasks = this._viewMode !== 'practicas';
        const tab = (mode, label) => `
            <button type="button" role="tab" class="tk-tab ${(mode === 'practicas') === !tasks ? 'is-on' : ''}" aria-selected="${(mode === 'practicas') === !tasks}"
                ${UIEvents.attr("click", function(event,captured){TasksAdminView.setViewMode(String(captured[0]));}, [mode])}>${label}</button>`;
        return `
        <div class="dnr-proposal tk-view">
            <header class="tk-head">
                <h1 class="tk-title">Gestión de tareas</h1>
                <div class="tk-tabs" role="tablist" aria-label="Secciones">${tab('tasks', 'Tareas')}${tab('practicas', 'Prácticas')}</div>
                ${tasks ? `<button type="button" class="tk-btn is-primary" ${UIEvents.attr("click", function(event,captured){TasksAdminView.openNewTaskModal();}, [])}><i class="fas fa-plus"></i> Nueva tarea</button>` : ''}
            </header>
            ${tasks ? `
            <div class="tk-layout">
                <nav class="tk-card tk-list-panel" aria-label="Tareas">
                    <div class="tk-list-tools">
                        <div id="task-archive-tabs" class="tk-seg"></div>
                        <input type="search" id="task-search-input" class="tk-input" placeholder="Buscar tarea" aria-label="Buscar tarea" value="${Utils.sanitizeHTML(this._searchQuery)}"
                            ${UIEvents.attr("input", function(event,captured){TasksAdminView.onSearchChange(this.value);}, [])}>
                        <select id="task-formation-select" class="tk-input" aria-label="Formación" ${UIEvents.attr("change", function(event,captured){TasksAdminView.setFormationFilter(this.value);}, [])}></select>
                    </div>
                    <div id="tasks-admin-list" class="tk-list"><p class="tk-empty">Cargando tareas…</p></div>
                </nav>
                <section class="tk-card tk-detail" id="tk-detail" aria-live="polite"></section>
            </div>` : `
            <div id="tasks-admin-list" style="width: 100%;"><p class="tk-empty">Cargando…</p></div>`}
        </div>`;
    },

    init() {
        this._pendingAttachment = null;
        this._searchQuery = '';
        this._selectedFormationFilter = 'ALL';
        if (this._viewMode === 'practicas') {
            this.loadPracticasGallery();
        } else {
            this.loadTasks();
        }
    },

    setViewMode(mode) {
        this._viewMode = mode;
        const container = document.getElementById('content-area');
        if (container) {
            SafeHTML.set(container, this.render());
            if (mode === 'practicas') {
                this.loadPracticasGallery();
            } else if (this._tasks.length) {
                this.renderTasksList();
            } else {
                this.loadTasks();
            }
        }
    },

    setFormationFilter(formationId) {
        this._selectedFormationFilter = formationId;
        if (this._viewMode === 'practicas') this.renderPracticasList(); else this.renderTasksList();
    },

    onSearchChange(query) {
        this._searchQuery = query.toLowerCase();
        if (this._viewMode === 'practicas') this.renderPracticasList(); else this.renderTasksList();
    },

    async loadTasks() {
        const container = document.getElementById('tasks-admin-list');
        if (!container) return;
        try {
            const token = AuthManager.getCsrfToken();
            const res = await fetch('/api/tasks', { headers: { 'X-SICAF-CSRF': token } });
            const data = await res.json();
            if (!data.success) throw new Error(data.error);
            this._tasks = data.tasks || [];
            this.renderTasksList();
        } catch(e) {
            if (container) SafeHTML.set(container, '<div style="padding: 2rem; color:#ef4444; font-weight:600;">Error al cargar tareas: ' + e.message + '</div>');
        }
    },

    // Day of the deadline for the list, the sentence for the detail and its tone.
    dueInfo(task, now = new Date()) {
        const deadline = task && task.deadline ? new Date(task.deadline) : null;
        if (!deadline || Number.isNaN(deadline.getTime())) return { short: 'Sin fecha', long: 'Sin fecha límite', tone: 'is-none', order: 2, time: Infinity };
        const days = Math.ceil((deadline - now) / 86400000);
        const day = deadline.toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric' }).replace('.', '');
        const long = deadline.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' }) + ' a las ' + deadline.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
        if (deadline < now) return { short: deadline.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' }).replace('.', ''), long: 'Plazo vencido el ' + long, tone: 'is-over', order: 3, time: -deadline.getTime() };
        const left = days <= 0 ? 'hoy' : days === 1 ? 'mañana' : `en ${days} días`;
        return { short: days <= 6 ? day : deadline.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' }).replace('.', ''), long: `Vence el ${long} (${left})`, tone: days <= 6 ? 'is-soon' : 'is-later', order: days <= 6 ? 0 : 1, time: deadline.getTime() };
    },

    taskAuthor(task) {
        let name = task.createdByName || 'Profesor / Admin';
        if (task.createdBy && typeof DataManager !== 'undefined' && DataManager.KEYS?.PROFESSORS) {
            const prof = (DataManager.getAll(DataManager.KEYS.PROFESSORS) || []).find(p => String(p.id) === String(task.createdBy));
            if (prof) name = `${prof.firstName || prof.name || ''} ${prof.lastName || ''}`.trim();
        }
        return name;
    },

    taskRecipients(task) {
        const formations = (typeof DataManager !== 'undefined' && DataManager.getFormations) ? DataManager.getFormations() : [];
        const students = (typeof DataManager !== 'undefined' && DataManager.getAll) ? (DataManager.getAll(DataManager.KEYS.STUDENTS) || []) : [];
        return [
            ...(task.formationIds || []).map(fid => formations.find(f => String(f.id) === String(fid))?.name).filter(Boolean),
            ...(task.studentIds || []).map(sid => { const s = students.find(st => String(st.id) === String(sid)); return s ? `${s.firstName} ${(s.lastName || '').charAt(0)}.` : ''; }).filter(Boolean)
        ];
    },

    selectTask(taskId) {
        this._selectedTaskId = taskId;
        this.renderTasksList();
    },

    renderTasksList() {
        const container = document.getElementById('tasks-admin-list');
        if (!container) return;
        const formations = (typeof DataManager !== 'undefined' && DataManager.getFormations) ? DataManager.getFormations() : [];
        const esc = value => Utils.sanitizeHTML(String(value ?? ''));

        const selectElem = document.getElementById('task-formation-select');
        if (selectElem) {
            SafeHTML.set(selectElem, `<option value="ALL" ${this._selectedFormationFilter === 'ALL' ? 'selected' : ''}>Todas las formaciones</option>` +
                formations.map(f => `<option value="${esc(f.id)}" ${String(this._selectedFormationFilter) === String(f.id) ? 'selected' : ''}>${esc(f.name)}</option>`).join(''));
        }

        this.renderArchiveTabs();
        let filtered = this._tasks.filter(t => (t.archived === true) === this._showArchived);
        if (this._selectedFormationFilter !== 'ALL') filtered = filtered.filter(t => (t.formationIds || []).map(String).includes(String(this._selectedFormationFilter)));
        if (this._searchQuery) filtered = filtered.filter(t => (t.title && t.title.toLowerCase().includes(this._searchQuery)) || (t.description && t.description.toLowerCase().includes(this._searchQuery)));

        const now = new Date();
        // Soonest first, then later ones, then without date, then the expired (most recent first).
        filtered = filtered.map(task => ({ task, due: this.dueInfo(task, now) }))
            .sort((a, b) => a.due.order - b.due.order || a.due.time - b.due.time)
            .map(({ task }) => task);

        if (!filtered.some(t => String(t.id) === String(this._selectedTaskId))) this._selectedTaskId = filtered[0]?.id || null;

        SafeHTML.set(container, filtered.length ? filtered.map(task => {
            const due = this.dueInfo(task, now);
            const on = String(task.id) === String(this._selectedTaskId);
            return `
                <button type="button" class="tk-item ${on ? 'is-on' : ''}" data-id="${esc(task.id)}" aria-pressed="${on}"
                    ${UIEvents.attr("click", function(event,captured){TasksAdminView.selectTask(String(captured[0]));}, [task.id])}>
                    <span class="tk-due ${due.tone}">${esc(due.short)}</span>
                    <span class="tk-item-main"><b>${esc(task.title)}</b><span>${esc(this.taskRecipients(task).join(' · ') || 'Sin destinatarios')}</span></span>
                </button>`;
        }).join('') : '<p class="tk-empty">No hay tareas con estos filtros. Pulsa «Nueva tarea» para crear una.</p>');

        this.renderTaskDetail(filtered.find(t => String(t.id) === String(this._selectedTaskId)) || null);
    },

    renderTaskDetail(task) {
        const box = document.getElementById('tk-detail');
        if (!box) return;
        if (!task) { SafeHTML.set(box, '<p class="tk-empty">Elige una tarea de la lista.</p>'); return; }
        const esc = value => Utils.sanitizeHTML(String(value ?? ''));
        const due = this.dueInfo(task);
        const canEdit = this.canEditTask(task);
        const recipients = this.taskRecipients(task);
        SafeHTML.set(box, `
            <header class="tk-detail-head">
                <div class="tk-detail-title">
                    <h2 class="tk-h2">${esc(task.title)}</h2>
                    <span class="tk-sub">Asignada por ${esc(this.taskAuthor(task))} · <b class="tk-due-text ${due.tone}">${esc(due.long)}</b></span>
                </div>
                ${canEdit ? `<button type="button" class="tk-btn" ${UIEvents.attr("click", function(event,captured){TasksAdminView.openNewTaskModal(String(captured[0]));}, [task.id])}><i class="fas fa-pen"></i> Editar</button>
                <button type="button" class="tk-btn" ${UIEvents.attr("click", function(event,captured){TasksAdminView.toggleArchive(String(captured[0]));}, [task.id])}><i class="fas ${task.archived === true ? 'fa-box-open' : 'fa-box-archive'}"></i> ${task.archived === true ? 'Recuperar' : 'Archivar'}</button>` : ''}
                <button type="button" class="tk-btn is-danger" ${UIEvents.attr("click", function(event,captured){TasksAdminView.deleteTask(String(captured[0]));}, [task.id])}><i class="fas fa-trash-can"></i> Eliminar</button>
            </header>
            <div class="tk-detail-grid">
                <div class="tk-detail-text">
                    <span class="tk-label">Enunciado</span>
                    <p class="tk-desc">${task.description ? esc(task.description) : '<span class="tk-muted">Sin enunciado.</span>'}</p>
                    ${task.attachmentName ? `<a class="tk-attach" href="/api/tasks/attachment/${encodeURIComponent(task.attachmentId)}" target="_blank" rel="noopener"><i class="fas fa-paperclip"></i> ${esc(task.attachmentName)}</a>` : ''}
                </div>
                <div class="tk-detail-facts">
                    <div><span class="tk-label">Para</span><span class="tk-chips">${recipients.length ? recipients.map(r => `<span class="tk-chip">${esc(r)}</span>`).join('') : '<span class="tk-muted">Sin destinatarios</span>'}</span></div>
                    <div><span class="tk-label">Formatos y tamaño</span><span class="tk-fact">${esc((task.allowedTypes || []).length ? task.allowedTypes.join(', ') : 'Todos')} · hasta ${esc(task.maxSizeMB || 50)} MB</span></div>
                </div>
            </div>
            <div class="tk-subs-wrap">
                <div class="tk-subs-head"><span class="tk-label">Entregas</span><span class="tk-subs-count" id="tk-subs-count"></span></div>
                <div id="tk-subs" class="tk-subs"><p class="tk-empty">Cargando entregas…</p></div>
            </div>`);
        this.loadTaskSubmissions(task);
    },

    // The submissions of the chosen task, drawn inside the page (same data as the old «Entregas» window).
    async loadTaskSubmissions(task) {
        let submissions = [];
        try {
            const res = await fetch('/api/tasks/' + encodeURIComponent(task.id) + '/submissions', { headers: { 'X-SICAF-CSRF': AuthManager.getCsrfToken() } });
            const data = await res.json();
            if (data.success) submissions = data.submissions || [];
        } catch (e) { /* the list below says nobody delivered; the server call can be retried by choosing the task again */ }
        if (String(task.id) !== String(this._selectedTaskId)) return;
        const box = document.getElementById('tk-subs');
        if (!box) return;
        const esc = value => Utils.sanitizeHTML(String(value ?? ''));
        const allStudents = (typeof DataManager !== 'undefined' && DataManager.getAll) ? (DataManager.getAll(DataManager.KEYS.STUDENTS) || []) : [];
        const byStudent = {};
        submissions.forEach(s => { (byStudent[s.studentId] = byStudent[s.studentId] || []).push(s); });
        const expected = [...new Set([
            ...(task.studentIds || []).map(String),
            ...allStudents.filter(s => s.formations && s.formations.some(fid => (task.formationIds || []).map(String).includes(String(fid)))).map(s => String(s.id))
        ])];
        const deadline = task.deadline ? new Date(task.deadline) : null;
        const formatSize = b => b < 1024 * 1024 ? (b / 1024).toFixed(1) + ' KB' : (b / (1024 * 1024)).toFixed(1) + ' MB';
        let delivered = 0;
        const rows = expected.map(sid => {
            const student = allStudents.find(s => String(s.id) === sid);
            const subs = (byStudent[sid] || []).sort((a, b) => new Date(b.uploadedAt) - new Date(a.uploadedAt));
            const latest = subs.find(s => !s.superseded) || subs[0];
            if (latest) delivered++;
            const late = latest && deadline && new Date(latest.uploadedAt) > deadline;
            return `
                <div class="tk-sub">
                    <b class="tk-sub-name">${esc(student ? `${student.firstName} ${student.lastName || ''}` : sid)}</b>
                    <span class="tk-state ${latest ? (late ? 'is-late' : 'is-done') : ''}">${latest ? (late ? 'Entregada tarde' : 'Entregada') : 'Sin entregar'}</span>
                    <span class="tk-sub-file">${latest ? `${esc(latest.originalName)} · ${formatSize(latest.size || 0)} · ${new Date(latest.uploadedAt).toLocaleDateString('es-ES')}${subs.length > 1 ? ` · ${subs.length} entregas` : ''}` : '—'}</span>
                    ${latest ? `<a class="tk-btn is-small" href="/api/student-works/${encodeURIComponent(sid)}/download/${encodeURIComponent(latest.id)}" target="_blank" rel="noopener"><i class="fas fa-arrow-down"></i> Descargar</a>` : ''}
                </div>`;
        }).join('');
        const count = document.getElementById('tk-subs-count');
        if (count) count.textContent = expected.length ? `${delivered} de ${expected.length} entregadas` : '';
        SafeHTML.set(box, rows || '<p class="tk-empty">No hay alumnos asignados a esta tarea.</p>');
    },

    setArchivedFilter(archived) {
        this._showArchived = !!archived;
        this.renderTasksList();
    },

    renderArchiveTabs() {
        const tabs = document.getElementById('task-archive-tabs');
        if (!tabs) return;
        const archived = (this._tasks || []).filter(t => t.archived === true).length;
        const active = (this._tasks || []).length - archived;
        const tab = (value, label, count) => `<button type="button" class="tk-seg-btn ${this._showArchived === value ? 'is-on' : ''}" ${UIEvents.attr("click", function(event,captured){TasksAdminView.setArchivedFilter(captured[0]);}, [value])}
            aria-pressed="${this._showArchived === value}">${label} (${count})</button>`;
        SafeHTML.set(tabs, tab(false, 'Activas', active) + tab(true, 'Archivadas', archived));
    },

    async toggleArchive(taskId) {
        const task = (this._tasks || []).find(t => String(t.id) === String(taskId));
        if (!this.canEditTask(task)) { Utils.showToast('Solo quien creó la tarea o administración pueden archivarla.', 'error'); return; }
        try {
            const res = await fetch('/api/tasks/' + encodeURIComponent(task.id), { method: 'PUT', headers: { 'X-SICAF-CSRF': AuthManager.getCsrfToken(), 'Content-Type': 'application/json' }, body: JSON.stringify({ archived: task.archived !== true }) });
            const data = await res.json().catch(() => ({ success: false, error: 'HTTP ' + res.status }));
            if (!data.success) throw new Error(data.error);
            Utils.showToast(task.archived === true ? 'Tarea recuperada' : 'Tarea archivada', 'success');
            this.loadTasks();
        } catch (e) { Utils.showToast('Error: ' + e.message, 'error'); }
    },

    // Same rule as the server: the author, admin/coordination or "Tareas y trabajos" managers.
    canEditTask(task) {
        const user = AuthManager.getCurrentUser();
        return !!task && (AuthManager.canManageModule('gestion-tareas') || (!!user && String(task.createdBy) === String(user.id)));
    },

    toLocalInput(iso) {
        const date = iso ? new Date(iso) : null;
        if (!date || Number.isNaN(date.getTime())) return '';
        return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
    },

    // Formats grouped by kind in the task form; each kind switches all of its formats on or off.
    TASK_FORMAT_KINDS: [
        ['Vídeo', ['mp4', 'mov', 'avi']],
        ['Documento', ['pdf', 'docx', 'xlsx']],
        ['Imagen', ['jpg', 'png']],
        ['Audio', ['mp3', 'wav']],
        ['Proyecto', ['premiere', 'after-effects', 'davinci', 'zip']]
    ],

    // Nueva / Editar tarea, diseño A (2026-10-27): ventana ancha en dos columnas — qué hay que hacer y
    // cómo se entrega a la izquierda; para quién (grupos por tipo y alumnos con buscador) a la derecha.
    // Estilos: assets/css/views/tasks.css (.tkf-*). Lo que se guarda no cambia.
    openNewTaskModal(taskId = null) {
        if (!AuthManager.requireModuleManage("gestion-tareas",["admin","coordinator","professor"])) return;
        const task = taskId ? (this._tasks || []).find(t => String(t.id) === String(taskId)) : null;
        if (taskId && !this.canEditTask(task)) { Utils.showToast('Solo quien creó la tarea o administración pueden editarla.', 'error'); return; }
        this._pendingAttachment = null;
        const esc = value => Utils.sanitizeHTML(String(value ?? ''));
        const checkedTypes = task ? (task.allowedTypes || []) : ['pdf', 'zip', 'mp4', 'docx'];
        const formations = (typeof DataManager !== 'undefined' && DataManager.getFormations) ? DataManager.getFormations() : [];
        const allStudents = (typeof DataManager !== 'undefined' && DataManager.getAll) ? (DataManager.getAll(DataManager.KEYS.STUDENTS) || []) : [];
        this._taskFormStudents = allStudents;
        const chosenFormations = (task?.formationIds || []).map(String);
        const chosenStudents = (task?.studentIds || []).map(String);

        // Groups ordered by the school's types (Plan de estudios → Tipos); unknown types last.
        const typeOrder = typeof FormationTypeService !== 'undefined' ? FormationTypeService.getTypes().map(t => t.id) : [];
        const byType = {};
        formations.forEach(f => { (byType[f.type || ''] = byType[f.type || ''] || []).push(f); });
        const typeIds = Object.keys(byType).sort((a, b) => (typeOrder.indexOf(a) + 1 || 99) - (typeOrder.indexOf(b) + 1 || 99));
        const typeLabel = id => (typeof FormationTypeService !== 'undefined' && id ? FormationTypeService.plural(id) : '') || 'Otras';
        const allFormats = [...new Set(this.TASK_FORMAT_KINDS.flatMap(([, list]) => list))];

        SafeHTML.fire({
            title: null,
            width: '1040px',
            customClass: { popup: 'task-modal-swal tkf-swal', confirmButton: 'tkf-submit', cancelButton: 'tkf-cancel' },
            buttonsStyling: false,
            html: `
            <div class="tkf dnr-proposal">
                <header class="tkf-head"><h2 class="tkf-title">${task ? 'Editar tarea' : 'Nueva tarea'}</h2></header>
                <div class="tkf-cols">
                    <div class="tkf-col">
                        <span class="tkf-step">1 · Qué hay que hacer</span>
                        <label class="tkf-field"><span class="tkf-label">Título *</span>
                            <input id="task-title" type="text" class="tkf-input" placeholder="Ej.: Escaleta del cortometraje" value="${esc(task?.title || '')}"></label>
                        <label class="tkf-field"><span class="tkf-label">Enunciado</span>
                            <textarea id="task-description" rows="6" class="tkf-input tkf-textarea" placeholder="Objetivos, indicaciones y cómo entregar">${esc(task?.description || '')}</textarea></label>
                        <div class="tkf-field"><span class="tkf-label">Material para el alumno</span>
                            <label class="tkf-drop" ${UIEvents.attr("dragover", function(event,captured){event.preventDefault();this.classList.add('is-over');}, [])} ${UIEvents.attr("dragleave", function(event,captured){this.classList.remove('is-over');}, [])} ${UIEvents.attr("drop", function(event,captured){TasksAdminView.taskFormDrop(event,this);}, [])}>
                                <b>Suelta aquí la guía o la plantilla</b>
                                <span>o pulsa para elegir · PDF, Word, PowerPoint o texto · se sube al guardar</span>
                                <input type="file" id="task-attachment-input" accept=".pdf,.doc,.docx,.ppt,.pptx,.txt" ${UIEvents.attr("change", function(event,captured){TasksAdminView.taskFormFileChosen(this);}, [])}>
                            </label>
                            <div id="task-attachment-status" class="tkf-hint">${task?.attachmentName ? `Adjunto actual: ${esc(task.attachmentName)}. Si eliges otro, lo sustituye.` : ''}</div>
                        </div>
                        <span class="tkf-step">3 · Cómo se entrega</span>
                        <div class="tkf-row">
                            <label class="tkf-field"><span class="tkf-label">Fecha y hora límite</span><input id="task-deadline" type="datetime-local" class="tkf-input" value="${this.toLocalInput(task?.deadline)}"></label>
                            <label class="tkf-field tkf-size"><span class="tkf-label">Tamaño máx.</span><span class="tkf-inline"><input id="task-maxsize" type="number" value="${Number(task?.maxSizeMB) || 100}" min="1" max="500" class="tkf-input"> MB</span></label>
                        </div>
                        <div class="tkf-field"><span class="tkf-label">Formatos aceptados</span>
                            <div class="tkf-chips" id="task-format-kinds">
                                ${this.TASK_FORMAT_KINDS.map(([kind, list]) => `<button type="button" class="tkf-chip" data-kind="${esc(kind)}" ${UIEvents.attr("click", function(event,captured){TasksAdminView.taskFormToggleKind(String(captured[0]));}, [kind])}>${esc(kind)} <span>${list.join(' · ')}</span></button>`).join('')}
                            </div>
                            <details class="tkf-more"><summary>Elegir formatos sueltos</summary>
                                <div class="tkf-chips">
                                    ${allFormats.map(t => `<label class="tkf-check"><input type="checkbox" value="${t}" class="task-type-check" ${checkedTypes.includes(t) ? 'checked' : ''} ${UIEvents.attr("change", function(event,captured){TasksAdminView.taskFormSyncKinds();}, [])}> .${t}</label>`).join('')}
                                </div>
                            </details>
                        </div>
                    </div>
                    <div class="tkf-col tkf-col-side">
                        <span class="tkf-step">2 · Para quién</span>
                        <div class="tkf-field"><span class="tkf-label">Grupos</span>
                            ${typeIds.map(type => `
                                <div class="tkf-type">
                                    <span class="tkf-type-name">${esc(typeLabel(type))}</span>
                                    <div class="tkf-chips">${byType[type].map(f => `<label class="tkf-check is-group"><input type="checkbox" value="${esc(f.id)}" class="task-formation-check" ${chosenFormations.includes(String(f.id)) ? 'checked' : ''} ${UIEvents.attr("change", function(event,captured){TasksAdminView.taskFormSummary();}, [])}> ${esc(f.name)}</label>`).join('')}</div>
                                </div>`).join('') || '<span class="tkf-hint">No hay formaciones.</span>'}
                        </div>
                        <div class="tkf-field"><span class="tkf-label">Alumnos sueltos</span>
                            <input id="task-student-search" type="search" class="tkf-input" placeholder="Busca un alumno por su nombre" autocomplete="off" aria-label="Buscar alumno"
                                ${UIEvents.attr("input", function(event,captured){TasksAdminView.taskFormSearchStudents(this.value);}, [])}>
                            <div id="task-student-results" class="tkf-results" role="listbox" aria-label="Alumnos encontrados"></div>
                            <div id="task-student-chips" class="tkf-chips">${chosenStudents.map(id => this.taskFormStudentChip(id)).join('')}</div>
                        </div>
                        <div class="tkf-summary" id="task-form-summary" aria-live="polite"></div>
                    </div>
                </div>
            </div>`,
            didOpen: () => { this.taskFormSyncKinds(); this.taskFormSummary(); document.getElementById('task-title')?.focus(); },
            showCancelButton: true,
            reverseButtons: true,
            confirmButtonText: task ? 'Guardar cambios' : 'Crear tarea',
            cancelButtonText: 'Cancelar',
            preConfirm: async () => {
                const title = document.getElementById('task-title').value.trim();
                if (!title) { Swal.showValidationMessage('El título es obligatorio'); return false; }
                // A chosen file is uploaded on save.
                const fileInput = document.getElementById('task-attachment-input');
                if (fileInput?.files?.[0] && TasksAdminView._pendingAttachment?.file !== fileInput.files[0]) {
                    if (!await TasksAdminView.uploadAttachment()) { Swal.showValidationMessage('No se pudo subir el archivo adjunto'); return false; }
                }
                const formationIds = [...document.querySelectorAll('.task-formation-check:checked')].map(c => c.value);
                const studentIds = [...document.querySelectorAll('#task-student-chips [data-student-id]')].map(el => el.dataset.studentId);
                const allowedTypes = [...document.querySelectorAll('.task-type-check:checked')].map(c => c.value);
                const deadline = document.getElementById('task-deadline').value;
                const maxSizeMB = parseInt(document.getElementById('task-maxsize').value) || 100;
                return { title, description: document.getElementById('task-description').value.trim(), formationIds, studentIds, deadline: deadline ? new Date(deadline).toISOString() : null, allowedTypes, maxSizeMB, attachmentId: TasksAdminView._pendingAttachment ? TasksAdminView._pendingAttachment.attachmentId : (task?.attachmentId || null), attachmentName: TasksAdminView._pendingAttachment ? TasksAdminView._pendingAttachment.attachmentName : (task?.attachmentName || null) };
            }
        }).then(async result => {
            if (!result.isConfirmed) return;
            try {
                const token = AuthManager.getCsrfToken();
                const url = task ? '/api/tasks/' + encodeURIComponent(task.id) : '/api/tasks';
                const res = await fetch(url, { method: task ? 'PUT' : 'POST', headers: { 'X-SICAF-CSRF': token, 'Content-Type': 'application/json' }, body: JSON.stringify(result.value) });
                const data = await res.json().catch(() => ({ success: false, error: 'HTTP ' + res.status }));
                if (!data.success) throw new Error(data.error);
                Utils.showToast(task ? 'Tarea actualizada correctamente' : 'Tarea creada correctamente', 'success');
                TasksAdminView.loadTasks();
            } catch(e) { Utils.showToast('Error: ' + e.message, 'error'); }
        });
    },

    taskFormStudentChip(id) {
        const s = (this._taskFormStudents || []).find(st => String(st.id) === String(id));
        if (!s) return '';
        const name = `${s.firstName || ''} ${s.lastName || ''}`.trim();
        return `<span class="tkf-pill" data-student-id="${Utils.sanitizeHTML(s.id)}">${Utils.sanitizeHTML(name)}<button type="button" aria-label="Quitar a ${Utils.sanitizeHTML(name)}" ${UIEvents.attr("click", function(event,captured){TasksAdminView.taskFormRemoveStudent(String(captured[0]));}, [s.id])}>✕</button></span>`;
    },

    taskFormSearchStudents(query) {
        const box = document.getElementById('task-student-results');
        if (!box) return;
        const norm = v => String(v || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
        const q = norm(query).trim();
        const picked = new Set([...document.querySelectorAll('#task-student-chips [data-student-id]')].map(el => el.dataset.studentId));
        const found = q ? (this._taskFormStudents || []).filter(s => !picked.has(String(s.id)) && norm(`${s.firstName} ${s.lastName}`).includes(q)).slice(0, 8) : [];
        SafeHTML.set(box, found.map(s => `<button type="button" role="option" class="tkf-result" ${UIEvents.attr("click", function(event,captured){TasksAdminView.taskFormAddStudent(String(captured[0]));}, [s.id])}>${Utils.sanitizeHTML(`${s.firstName || ''} ${s.lastName || ''}`.trim())}</button>`).join('')
            || (q ? '<span class="tkf-hint">Ningún alumno con ese nombre.</span>' : ''));
    },

    taskFormAddStudent(id) {
        const chips = document.getElementById('task-student-chips');
        if (!chips) return;
        const ids = [...chips.querySelectorAll('[data-student-id]')].map(el => el.dataset.studentId);
        if (ids.includes(String(id))) return;
        SafeHTML.set(chips, ids.concat([String(id)]).map(sid => this.taskFormStudentChip(sid)).join(''));
        const search = document.getElementById('task-student-search');
        if (search) { search.value = ''; search.focus(); }
        this.taskFormSearchStudents('');
        this.taskFormSummary();
    },

    taskFormRemoveStudent(id) {
        const chips = document.getElementById('task-student-chips');
        if (!chips) return;
        SafeHTML.set(chips, [...chips.querySelectorAll('[data-student-id]')].filter(el => el.dataset.studentId !== String(id)).map(el => this.taskFormStudentChip(el.dataset.studentId)).join(''));
        this.taskFormSummary();
    },

    // «2 grupos y 1 alumno suelto · 19 alumnos» — the distinct students who will get the task.
    taskFormSummary() {
        const box = document.getElementById('task-form-summary');
        if (!box) return;
        const groups = [...document.querySelectorAll('.task-formation-check:checked')].map(c => c.value);
        const loose = [...document.querySelectorAll('#task-student-chips [data-student-id]')].map(el => el.dataset.studentId);
        const ids = new Set(loose);
        (this._taskFormStudents || []).forEach(s => { if ((s.formations || []).some(fid => groups.includes(String(fid)))) ids.add(String(s.id)); });
        const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
        const parts = [groups.length ? plural(groups.length, 'grupo', 'grupos') : '', loose.length ? plural(loose.length, 'alumno suelto', 'alumnos sueltos') : ''].filter(Boolean);
        box.textContent = parts.length
            ? `${parts.join(' y ')} · ${plural(ids.size, 'alumno recibirá', 'alumnos recibirán')} el aviso`
            : 'Elige al menos un grupo o un alumno.';
    },

    taskFormToggleKind(kind) {
        const list = (this.TASK_FORMAT_KINDS.find(([k]) => k === kind) || [null, []])[1];
        const boxes = [...document.querySelectorAll('.task-type-check')].filter(c => list.includes(c.value));
        const allOn = boxes.length && boxes.every(c => c.checked);
        boxes.forEach(c => { c.checked = !allOn; });
        this.taskFormSyncKinds();
    },

    // A kind is shown on when all its formats are chosen, half when only some are.
    taskFormSyncKinds() {
        document.querySelectorAll('#task-format-kinds [data-kind]').forEach(btn => {
            const list = (this.TASK_FORMAT_KINDS.find(([k]) => k === btn.dataset.kind) || [null, []])[1];
            const boxes = [...document.querySelectorAll('.task-type-check')].filter(c => list.includes(c.value));
            const on = boxes.filter(c => c.checked).length;
            btn.classList.toggle('is-on', on > 0 && on === boxes.length);
            btn.classList.toggle('is-part', on > 0 && on < boxes.length);
            btn.setAttribute('aria-pressed', String(on > 0 && on === boxes.length));
        });
    },

    taskFormFileChosen(input) {
        const status = document.getElementById('task-attachment-status');
        if (status && input.files && input.files[0]) status.textContent = `Se subirá al guardar: ${input.files[0].name}`;
    },

    taskFormDrop(event, zone) {
        event.preventDefault();
        zone.classList.remove('is-over');
        const input = document.getElementById('task-attachment-input');
        const file = event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files[0];
        if (!input || !file) return;
        const transfer = new DataTransfer();
        transfer.items.add(file);
        input.files = transfer.files;
        this.taskFormFileChosen(input);
    },

    async uploadAttachment() {
        if (!AuthManager.requireModuleManage("gestion-tareas",["admin","coordinator","professor"])) return;
        const input = document.getElementById('task-attachment-input');
        const status = document.getElementById('task-attachment-status');
        if (!input || !input.files || !input.files[0]) { if (status) SafeHTML.set(status, '<span style="color:#ef4444;">Selecciona un archivo primero</span>'); return false; }
        if (status) SafeHTML.set(status, '<i class="fas fa-spinner fa-spin"></i> Subiendo...');
        try {
            const formData = new FormData();
            formData.append('file', input.files[0]);
            const token = AuthManager.getCsrfToken();
            const res = await fetch('/api/tasks/attachment', { method: 'POST', headers: { 'X-SICAF-CSRF': token }, body: formData });
            const data = await res.json();
            if (!data.success) throw new Error(data.error);
            TasksAdminView._pendingAttachment = { attachmentId: data.attachmentId, attachmentName: data.attachmentName, file: input.files[0] };
            if (status) SafeHTML.set(status, '<span style="color:#10b981;"><i class="fas fa-check-circle"></i> ' + Utils.sanitizeHTML(data.attachmentName) + ' subido correctamente</span>');
            return true;
        } catch(e) { if (status) SafeHTML.set(status, '<span style="color:#ef4444;">Error: ' + Utils.sanitizeHTML(e.message) + '</span>'); return false; }
    },

    async deleteTask(taskId) {
        if (!AuthManager.requireModuleManage("gestion-tareas",["admin","coordinator","professor"])) return;
        const result = await SafeHTML.fire({ title: '¿Eliminar tarea?', text: 'Esta acción no se puede deshacer.', icon: 'warning', showCancelButton: true, confirmButtonColor: '#ef4444', confirmButtonText: 'Eliminar', cancelButtonText: 'Cancelar' });
        if (!result.isConfirmed) return;
        try {
            const token = AuthManager.getCsrfToken();
            const res = await fetch('/api/tasks/' + taskId, { method: 'DELETE', headers: { 'X-SICAF-CSRF': token } });
            const data = await res.json();
            if (!data.success) throw new Error(data.error);
            Utils.showToast('Tarea eliminada', 'info');
            TasksAdminView.loadTasks();
        } catch(e) { Utils.showToast('Error: ' + e.message, 'error'); }
    },

    // ── Pestaña "Prácticas" — Sistema YouTube Cinema Hub y Player ───────────
    _works: [],
    _worksIndex: {},
    _practicasFormationFilter: 'ALL',
    _practicasSearch: '',
    _currentWatchKey: null,
    _watchSidebarFilter: 'ALL', // 'ALL' | 'FORMATION' | 'TASK'
    _escWatchHandler: null,

    async loadPracticasGallery() {
        const container = document.getElementById('tasks-admin-list');
        if (!container) return;
        this._currentWatchKey = null;
        SafeHTML.set(container, '<section class="pv-card pv-empty"><i class="fas fa-spinner fa-spin" aria-hidden="true"></i><p>Cargando la videoteca…</p></section>');

        try {
            const token = AuthManager.getCsrfToken();
            const res = await fetch('/api/tasks/works-gallery', { headers: { 'X-SICAF-CSRF': token } });
            const data = await res.json();
            if (!data.success) throw new Error(data.error);
            
            // En esta sección únicamente deben verse vídeos; se descarta cualquier otro archivo
            this._works = (data.works || []).filter(w => (w.mimeType || '').startsWith('video/'));
            
            // Reconstruir el índice rápido por clave 'studentId:workId'
            this._worksIndex = {};
            this._works.forEach(w => {
                this._worksIndex[`${w.studentId}:${w.id}`] = w;
            });

            this.renderPracticasList();
        } catch (e) {
            SafeHTML.set(container, `<section class="pv-card pv-empty"><i class="fas fa-triangle-exclamation" aria-hidden="true"></i><p>No se pudo cargar la videoteca: ${Utils.sanitizeHTML(e.message)}</p></section>`);
        }
    },

    // Prácticas (2026-10-27): la galería clara con estanterías por formación (propuesta A) y, al pulsar un
    // vídeo, la lista a la izquierda y el reproductor a la derecha dentro de la página (propuesta C).
    // Al pasar el ratón por una miniatura se ven unos segundos del vídeo. Estilos: tasks.css (.pv-*).
    _videoWorks() {
        return (this._works || []).filter(w => (w.mimeType || '').startsWith('video/'));
    },

    renderPracticasList() {
        const container = document.getElementById('tasks-admin-list');
        if (!container) return;
        const current = this._currentWatchKey ? (this._worksIndex || {})[this._currentWatchKey] : null;
        if (current) { this._renderWatch(current); return; }
        this._currentWatchKey = null;

        const works = this._videoWorks();
        const formations = [...new Set(works.flatMap(w => w.formationNames || []))].sort();
        const chip = (value, label, count) => {
            const on = this._practicasFormationFilter === value;
            return `<button type="button" class="pv-chip ${on ? 'is-on' : ''}" aria-pressed="${on}"
                ${UIEvents.attr("click", function(event,captured){TasksAdminView.setPracticasFormationFilter(String(captured[0]));}, [on && value !== 'ALL' ? 'ALL' : value])}>${Utils.sanitizeHTML(label)} <span>${count}</span></button>`;
        };
        SafeHTML.set(container, `
            <div class="pv-gallery">
                <div class="pv-bar">
                    <span class="pv-bar-title">${Utils.sanitizeHTML(SchoolService.label('Videoteca'))} · ${works.length} ${works.length === 1 ? 'vídeo' : 'vídeos'}</span>
                    <div class="pv-chips" role="group" aria-label="Formación">
                        ${chip('ALL', 'Todos', works.length)}
                        ${formations.map(fn => chip(fn, fn, works.filter(w => (w.formationNames || []).includes(fn)).length)).join('')}
                    </div>
                    <label class="pv-search">
                        <i class="fas fa-search" aria-hidden="true"></i>
                        <input type="search" placeholder="Buscar por título, alumno, formación o tarea" aria-label="Buscar vídeo" value="${Utils.sanitizeHTML(this._practicasSearch)}"
                            ${UIEvents.attr("input", function(event,captured){TasksAdminView.onPracticasSearch(this.value);}, [])}>
                    </label>
                </div>
                <div id="pv-body">${this._renderPracticasBody(this._filteredPracticas())}</div>
            </div>`);
    },

    _filteredPracticas() {
        let works = this._videoWorks();
        const query = (this._practicasSearch || '').trim().toLowerCase();
        if (query) {
            works = works.filter(w => [w.title, w.studentName, w.taskTitle, w.notes, ...(w.formationNames || [])].some(text => text && String(text).toLowerCase().includes(query)));
        }
        if (this._practicasFormationFilter !== 'ALL') works = works.filter(w => (w.formationNames || []).includes(this._practicasFormationFilter));
        return works.slice().sort((a, b) => new Date(b.uploadedAt) - new Date(a.uploadedAt));
    },

    _renderPracticasBody(works) {
        if (works.length === 0) {
            return `
                <section class="pv-card pv-empty">
                    <i class="fas fa-video-slash" aria-hidden="true"></i>
                    <h3>No hay vídeos</h3>
                    <p>${this._videoWorks().length ? 'Ningún vídeo coincide con la búsqueda o el filtro.' : 'Cuando los alumnos entreguen vídeos en sus tareas, aparecerán aquí.'}</p>
                    ${this._videoWorks().length ? `<button type="button" class="pv-btn" ${UIEvents.attr("click", function(event,captured){TasksAdminView._practicasFormationFilter='ALL';TasksAdminView._practicasSearch='';TasksAdminView.renderPracticasList();}, [])}><i class="fas fa-rotate-left"></i> Quitar filtros</button>` : ''}
                </section>`;
        }
        // Without filter or search: one shelf per formation; otherwise one grid.
        if (this._practicasFormationFilter === 'ALL' && !this._practicasSearch) {
            const groups = {};
            works.forEach(w => (w.formationNames && w.formationNames.length ? w.formationNames : ['Sin formación']).forEach(name => { (groups[name] = groups[name] || []).push(w); }));
            return Object.keys(groups).sort().map(name => `
                <section class="pv-card pv-shelf">
                    <header class="pv-shelf-head">
                        <h2 class="pv-h2">${Utils.sanitizeHTML(name)}</h2>
                        <span class="pv-count">${groups[name].length} ${groups[name].length === 1 ? 'vídeo' : 'vídeos'}</span>
                        <button type="button" class="pv-btn" ${UIEvents.attr("click", function(event,captured){TasksAdminView.openPracticaModal(String(captured[0]));}, [`${groups[name][0].studentId}:${groups[name][0].id}`])}><i class="fas fa-play"></i> Ver seguidos</button>
                    </header>
                    <div class="pv-grid">${groups[name].map(w => this._renderPracticaCard(w)).join('')}</div>
                </section>`).join('');
        }
        return `<section class="pv-card pv-shelf"><div class="pv-grid">${works.map(w => this._renderPracticaCard(w)).join('')}</div></section>`;
    },

    _renderPracticaCard(w) {
        const key = `${w.studentId}:${w.id}`;
        const inlineSrc = `/api/student-works/${encodeURIComponent(w.studentId)}/download/${encodeURIComponent(w.id)}?inline=1`;
        const initials = this._initials(w.studentName);
        return `
            <article class="pv-video-card ${w.superseded ? 'is-superseded' : ''}" data-key="${Utils.sanitizeHTML(key)}">
                <button type="button" class="pv-thumb" aria-label="Ver ${Utils.sanitizeHTML(w.title || 'vídeo')} de ${Utils.sanitizeHTML(w.studentName)}"
                    ${UIEvents.attr("click", function(event,captured){TasksAdminView.openPracticaModal(String(captured[0]));}, [key])}
                    ${UIEvents.attr("mouseenter", function(event,captured){TasksAdminView._hoverPlay(this);}, [])}
                    ${UIEvents.attr("mouseleave", function(event,captured){TasksAdminView._hoverStop(this);}, [])}
                    ${UIEvents.attr("focus", function(event,captured){TasksAdminView._hoverPlay(this);}, [])}
                    ${UIEvents.attr("blur", function(event,captured){TasksAdminView._hoverStop(this);}, [])}>
                    <video src="${inlineSrc}" preload="metadata" muted playsinline tabindex="-1" ${UIEvents.attr("loadedmetadata", function(event,captured){TasksAdminView._setDurationBadge(this);}, [])}></video>
                    <span class="pv-duration practica-duration-badge">--:--</span>
                    <span class="pv-preview-bar" aria-hidden="true"><i></i></span>
                    <span class="pv-play" aria-hidden="true"><i class="fas fa-play"></i></span>
                </button>
                <div class="pv-meta">
                    <span class="pv-avatar" style="background: ${this._avatarColor(w.studentName)};" aria-hidden="true">${Utils.sanitizeHTML(initials)}</span>
                    <span class="pv-meta-text">
                        <b>${Utils.sanitizeHTML(w.title || 'Sin título')}</b>
                        <span>${Utils.sanitizeHTML(w.studentName)}</span>
                        <span class="pv-sub">${w.taskTitle ? Utils.sanitizeHTML(w.taskTitle) + ' · ' : ''}${this._timeAgo(w.uploadedAt)}${w.superseded ? ' · <em>sustituida</em>' : ''}</span>
                    </span>
                    <button type="button" class="pv-more" aria-label="Más opciones" title="Más opciones" ${UIEvents.attr("click", function(event,captured){event.stopPropagation();TasksAdminView.openQuickMenu(String(captured[0]),event);}, [key])}><i class="fas fa-ellipsis-vertical"></i></button>
                </div>
            </article>`;
    },

    // Opening a video switches the tab to the player view (list left, player right).
    openPracticaModal(key) {
        const w = (this._worksIndex || {})[key];
        if (!w) return;
        this._currentWatchKey = key;
        if (this._escWatchHandler) window.removeEventListener('keydown', this._escWatchHandler);
        this._escWatchHandler = event => { if (event.key === 'Escape' && !document.fullscreenElement) this.closePracticaModal(); };
        window.addEventListener('keydown', this._escWatchHandler);
        this._renderWatch(w);
        const area = document.getElementById('content-area');
        if (area) area.scrollTop = 0;
    },

    closePracticaModal() {
        const video = document.getElementById('yt-watch-video');
        if (video) video.pause();
        this._currentWatchKey = null;
        if (this._escWatchHandler) {
            window.removeEventListener('keydown', this._escWatchHandler);
            this._escWatchHandler = null;
        }
        if (this._viewMode === 'practicas' && document.getElementById('tasks-admin-list')) this.renderPracticasList();
    },

    _renderWatch(w) {
        const container = document.getElementById('tasks-admin-list');
        if (!container) return;
        const esc = value => Utils.sanitizeHTML(String(value ?? ''));
        const key = `${w.studentId}:${w.id}`;
        const downloadSrc = `/api/student-works/${encodeURIComponent(w.studentId)}/download/${encodeURIComponent(w.id)}`;
        const formations = (w.formationNames || []).join(' · ');
        let list = this._videoWorks();
        if (this._watchSidebarFilter === 'FORMATION') list = list.filter(it => (it.formationNames || []).some(fn => (w.formationNames || []).includes(fn)));
        else if (this._watchSidebarFilter === 'TASK' && w.taskId) list = list.filter(it => it.taskId === w.taskId);
        list = list.slice().sort((a, b) => new Date(b.uploadedAt) - new Date(a.uploadedAt));
        const filter = (value, label) => `<button type="button" class="pv-chip ${this._watchSidebarFilter === value ? 'is-on' : ''}" aria-pressed="${this._watchSidebarFilter === value}"
            ${UIEvents.attr("click", function(event,captured){TasksAdminView.setWatchSidebarFilter(String(captured[0]));}, [value])}>${label}</button>`;
        SafeHTML.set(container, `
            <div class="pv-watch">
                <div class="pv-watch-head">
                    <button type="button" class="pv-btn" ${UIEvents.attr("click", function(event,captured){TasksAdminView.closePracticaModal();}, [])}><i class="fas fa-arrow-left"></i> Volver a la videoteca</button>
                </div>
                <div class="pv-watch-layout">
                    <nav class="pv-card pv-watch-list" aria-label="Vídeos">
                        <div class="pv-watch-filters">${filter('ALL', 'Todos')}${filter('FORMATION', 'Misma formación')}${w.taskId ? filter('TASK', 'Misma tarea') : ''}</div>
                        <div class="pv-watch-items">
                            ${list.map(item => {
                                const itemKey = `${item.studentId}:${item.id}`;
                                const on = itemKey === key;
                                return `
                                    <button type="button" class="pv-watch-item ${on ? 'is-on' : ''}" aria-pressed="${on}" ${UIEvents.attr("click", function(event,captured){TasksAdminView.switchWatchVideo(String(captured[0]));}, [itemKey])}>
                                        <span class="pv-watch-thumb">
                                            <video src="/api/student-works/${encodeURIComponent(item.studentId)}/download/${encodeURIComponent(item.id)}?inline=1" preload="metadata" muted playsinline tabindex="-1" ${UIEvents.attr("loadedmetadata", function(event,captured){TasksAdminView._setDurationBadge(this);}, [])}></video>
                                            <span class="pv-duration practica-duration-badge">--:--</span>
                                        </span>
                                        <span class="pv-meta-text"><b>${esc(item.title || 'Sin título')}</b><span>${esc(item.studentName)}</span><span class="pv-sub">${item.taskTitle ? esc(item.taskTitle) + ' · ' : ''}${this._timeAgo(item.uploadedAt)}</span></span>
                                    </button>`;
                            }).join('')}
                        </div>
                    </nav>
                    <section class="pv-card pv-player-card">
                        <div class="pv-player"><video id="yt-watch-video" src="${downloadSrc}?inline=1" controls autoplay playsinline></video></div>
                        <div class="pv-player-head">
                            <div class="pv-player-title">
                                <h2 class="pv-h2">${esc(w.title || 'Sin título')}</h2>
                                <span class="pv-sub">${esc(w.studentName)}${formations ? ' · ' + esc(formations) : ''} · subido ${this._timeAgo(w.uploadedAt)}${w.superseded ? ' · <b class="pv-old">sustituido por una entrega nueva</b>' : ''}</span>
                            </div>
                            <button type="button" class="pv-btn" ${UIEvents.attr("click", function(event,captured){TasksAdminView.goToStudentProfile(String(captured[0]));}, [w.studentId])}><i class="fas fa-user-graduate"></i> Ficha del alumno</button>
                            ${w.taskId ? `<button type="button" class="pv-btn" ${UIEvents.attr("click", function(event,captured){TasksAdminView.viewTaskFromPractica(String(captured[0]));}, [w.taskId])}><i class="fas fa-list-check"></i> Ver la tarea</button>` : ''}
                            <button type="button" class="pv-btn" ${UIEvents.attr("click", function(event,captured){TasksAdminView.copyPracticaLink(String(captured[0]));}, [downloadSrc])}><i class="fas fa-link"></i> Copiar enlace</button>
                            <a class="pv-btn is-primary" href="${downloadSrc}" download><i class="fas fa-download"></i> Descargar (${this._formatBytes(w.size)})</a>
                            <button type="button" class="pv-btn" aria-label="Pantalla completa" title="Pantalla completa" ${UIEvents.attr("click", function(event,captured){TasksAdminView.toggleFullscreenVideo();}, [])}><i class="fas fa-expand"></i></button>
                        </div>
                        <div class="pv-player-facts">
                            <div><span class="pv-label">Tarea</span><span>${esc(w.taskTitle || 'Entrega sin tarea')}</span></div>
                            <div><span class="pv-label">Formación</span><span>${esc(formations || 'Sin formación')}</span></div>
                            <div><span class="pv-label">Formato</span><span>${esc(w.mimeType || 'Vídeo')}</span></div>
                        </div>
                        ${w.notes ? `<div class="pv-player-text"><span class="pv-label">Nota del alumno</span><p>${esc(w.notes)}</p></div>` : ''}
                        ${w.taskDescription ? `<div class="pv-player-text"><span class="pv-label">Enunciado de la tarea</span><p>${esc(w.taskDescription)}</p></div>` : ''}
                    </section>
                </div>
            </div>`);
    },

    switchWatchVideo(key) {
        const w = (this._worksIndex || {})[key];
        if (!w) return;
        this._currentWatchKey = key;
        this._renderWatch(w);
        const area = document.getElementById('content-area');
        if (area) area.scrollTop = 0;
    },

    setWatchSidebarFilter(filter) {
        this._watchSidebarFilter = filter;
        const w = this._currentWatchKey ? (this._worksIndex || {})[this._currentWatchKey] : null;
        if (w) this._renderWatch(w);
    },

    // Only the shelves are redrawn while typing, so the search box keeps the focus.
    onPracticasSearch(query) {
        this._practicasSearch = query;
        const body = document.getElementById('pv-body');
        if (body) SafeHTML.set(body, this._renderPracticasBody(this._filteredPracticas()));
        else this.renderPracticasList();
    },

    setPracticasFormationFilter(formationName) {
        this._practicasFormationFilter = formationName;
        this.renderPracticasList();
    },

    downloadWork(key) {
        const w = (this._worksIndex || {})[key];
        if (!w) return;
        const a = document.createElement('a');
        a.href = `/api/student-works/${encodeURIComponent(w.studentId)}/download/${encodeURIComponent(w.id)}`;
        a.download = (w.title || 'practica') + '.mp4';
        document.body.appendChild(a);
        a.click();
        a.remove();
    },

    copyPracticaLink(url) {
        const fullUrl = window.location.origin + url;
        navigator.clipboard.writeText(fullUrl).then(() => {
            Utils.showToast('Enlace de descarga copiado al portapapeles', 'success');
        }).catch(() => {
            Utils.showToast('No se pudo copiar el enlace: ' + fullUrl, 'error');
        });
    },

    toggleFullscreenVideo() {
        const video = document.getElementById('yt-watch-video');
        if (!video) return;
        if (!document.fullscreenElement) {
            if (video.requestFullscreen) video.requestFullscreen();
            else if (video.webkitRequestFullscreen) video.webkitRequestFullscreen();
        } else if (document.exitFullscreen) {
            document.exitFullscreen();
        }
    },

    goToStudentProfile(studentId) {
        this.closePracticaModal();
        if (typeof App !== 'undefined' && App.navigate) App.navigate('alumno/' + studentId);
    },

    viewTaskFromPractica(taskId) {
        this.closePracticaModal();
        this._selectedTaskId = taskId;
        this.setViewMode('tasks');
    },

    openQuickMenu(key) {
        const w = (this._worksIndex || {})[key];
        if (!w) return;
        SafeHTML.fire({
            title: Utils.sanitizeHTML(w.title || 'Opciones del vídeo'),
            html: `
                <div style="text-align:left; display:flex; flex-direction:column; gap:8px;">
                    <button class="btn btn-primary" ${UIEvents.attr("click", function(event,captured){Swal.close();TasksAdminView.openPracticaModal(String(captured[0]));}, [key])} style="justify-content:flex-start; width:100%; gap:10px;"><i class="fas fa-play"></i> Ver el vídeo</button>
                    <button class="btn btn-secondary" ${UIEvents.attr("click", function(event,captured){Swal.close();TasksAdminView.downloadWork(String(captured[0]));}, [key])} style="justify-content:flex-start; width:100%; gap:10px;"><i class="fas fa-download"></i> Descargar (${this._formatBytes(w.size)})</button>
                    ${w.taskId ? `<button class="btn btn-secondary" ${UIEvents.attr("click", function(event,captured){Swal.close();TasksAdminView.viewTaskFromPractica(String(captured[0]));}, [w.taskId])} style="justify-content:flex-start; width:100%; gap:10px;"><i class="fas fa-list-check"></i> Ver la tarea (${Utils.sanitizeHTML(w.taskTitle || 'Tarea')})</button>` : ''}
                    <button class="btn btn-secondary" ${UIEvents.attr("click", function(event,captured){Swal.close();TasksAdminView.goToStudentProfile(String(captured[0]));}, [w.studentId])} style="justify-content:flex-start; width:100%; gap:10px;"><i class="fas fa-user-graduate"></i> Ficha de ${Utils.sanitizeHTML(w.studentName)}</button>
                </div>`,
            showConfirmButton: false,
            showCancelButton: true,
            cancelButtonText: 'Cerrar',
            buttonsStyling: false,
            customClass: { cancelButton: 'btn btn-secondary' },
            width: '440px'
        });
    },

    // ── Utilidades de Formato y UI ───────────────────────────────────────────
    _initials(name) {
        if (!name) return '?';
        return name.trim().split(/\s+/).map(p => p.charAt(0)).slice(0, 2).join('').toUpperCase();
    },

    _avatarColor(name) {
        if (!name) return 'linear-gradient(135deg, #0284c7, #0f766e)';
        const colors = [
            'linear-gradient(135deg, #ef4444, #b91c1c)', // Red
            'linear-gradient(135deg, #0284c7, #0369a1)', // Blue
            'linear-gradient(135deg, #0d9488, #0f766e)', // Teal
            'linear-gradient(135deg, #8b5cf6, #6d28d9)', // Purple
            'linear-gradient(135deg, #f59e0b, #d97706)', // Amber
            'linear-gradient(135deg, #ec4899, #be185d)', // Pink
            'linear-gradient(135deg, #10b981, #059669)'  // Emerald
        ];
        let hash = 0;
        for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
        const index = Math.abs(hash) % colors.length;
        return colors[index];
    },

    _timeAgo(dateStr) {
        if (!dateStr) return '';
        const now = new Date();
        const date = new Date(dateStr);
        const diffMs = now - date;
        const diffSecs = Math.floor(diffMs / 1000);
        const diffMins = Math.floor(diffSecs / 60);
        const diffHours = Math.floor(diffMins / 60);
        const diffDays = Math.floor(diffHours / 24);
        const diffWeeks = Math.floor(diffDays / 7);
        const diffMonths = Math.floor(diffDays / 30);

        if (diffDays < 1) {
            if (diffHours < 1) return 'hace un momento';
            return `hace ${diffHours} ${diffHours === 1 ? 'hora' : 'horas'}`;
        }
        if (diffDays < 7) {
            return `hace ${diffDays} ${diffDays === 1 ? 'día' : 'días'}`;
        }
        if (diffWeeks < 4) {
            return `hace ${diffWeeks} ${diffWeeks === 1 ? 'semana' : 'semanas'}`;
        }
        if (diffMonths < 12) {
            return `hace ${diffMonths} ${diffMonths === 1 ? 'mes' : 'meses'}`;
        }
        return `hace más de 1 año`;
    },

    _formatBytes(bytes) {
        if (!bytes || isNaN(bytes)) return '0 B';
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
        if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
        return (bytes / (1024 * 1024 * 1024)).toFixed(2) + ' GB';
    },

    // Thumbnail: the duration, and a frame a little in so it is not black.
    _setDurationBadge(videoEl) {
        if (!isFinite(videoEl.duration)) return;
        const badge = videoEl.parentElement.querySelector('.practica-duration-badge');
        if (badge) {
            const total = Math.round(videoEl.duration);
            badge.textContent = `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
        }
        if (videoEl.paused && videoEl.id !== 'yt-watch-video') {
            try { videoEl.currentTime = Math.min(1, videoEl.duration / 10); } catch (e) { /* not seekable yet */ }
        }
    },

    // Preview like YouTube: after a short pause on the thumbnail, a few seconds from a little
    // into the video play muted and repeat until the pointer leaves.
    PREVIEW_SECONDS: 5,
    _hoverPlay(thumbEl) {
        const video = thumbEl.querySelector('video');
        if (!video) return;
        this._hoverStop(thumbEl);
        thumbEl._pvDelay = setTimeout(() => {
            const d = video.duration;
            const start = isFinite(d) && d > this.PREVIEW_SECONDS * 2 ? Math.min(d * 0.15, d - this.PREVIEW_SECONDS - 1) : 0;
            const restart = () => { try { video.currentTime = start; } catch (e) { /* not seekable yet */ } video.play().catch(() => {}); };
            restart();
            thumbEl.classList.add('is-previewing');
            thumbEl._pvLoop = setInterval(restart, this.PREVIEW_SECONDS * 1000);
        }, 350);
    },

    _hoverStop(thumbEl) {
        clearTimeout(thumbEl._pvDelay);
        clearInterval(thumbEl._pvLoop);
        thumbEl.classList.remove('is-previewing');
        const video = thumbEl.querySelector('video');
        if (!video) return;
        video.pause();
        if (isFinite(video.duration)) { try { video.currentTime = Math.min(1, video.duration / 10); } catch (e) { /* not seekable yet */ } }
    }
};

window.TasksAdminView = TasksAdminView;
